/**
 * The magic-link token that signs a worker in travels as `?token=` on the login route,
 * and it is a live credential: whoever reads it becomes that worker.
 *
 * The browser hands it out in four places at once, measured on prod over the 24 hours
 * to 14/08/2026: the address bar, the session history entry, the referrer of every
 * later request, and any collector that reads the URL. RUM alone shipped it in 35,996
 * `view_url`, 1,057 `view_referrer` and 724 `resource_url` events.
 *
 * Two defences live here, and neither replaces the other:
 *
 * - `consumeLoginTokenFromUrl` takes the token out of the address bar before anything
 *   else can read the URL. It is the only one of the two that reaches history and the
 *   referrer, which no filter on our own telemetry can touch.
 * - `redactUrlToken` rewrites the value inside a URL a collector has already captured.
 *   The initial document reaches RUM from the PerformanceNavigationTiming entry, and
 *   that entry keeps the URL the page was opened with: `history.replaceState` does not
 *   rewrite it, so redaction is the only thing that can.
 */

export const LOGIN_TOKEN_PARAM = 'token';
const REDACTED_TOKEN = 'REDACTED';

/**
 * `?token=` is not only the magic link: `/impostazioni` carries the e-mail change token
 * and that screen still reads it from the URL. So the address-bar cleanup, which changes
 * how the app behaves, is restricted to the login route (plus the locale prefix Next puts
 * in front of it, e.g. `/en/login`). Redaction below stays deliberately broad instead:
 * dropping a value from what we ship to OpenObserve cannot break a flow.
 */
const LOGIN_PATHNAME = /^(?:\/[a-z]{2})?\/login\/?$/;

/** Matches the parameter only at a query boundary, so `access_token=` is left alone. */
const TOKEN_IN_QUERY = new RegExp(`([?&]${LOGIN_TOKEN_PARAM}=)[^&#]*`, 'g');

/**
 * The same parameter, percent-encoded, because our URL is routinely carried INSIDE another
 * URL: an analytics beacon puts the page address in `dl=`/`dr=`/`ref=`, and a login link
 * puts the destination in `redirect_url=`. The `?` and `=` around the parameter arrive as
 * `%3F` and `%3D`, so the raw pattern above cannot see it and the value ships in clear.
 * Measured on prod over the 24 hours to 04/09/2026, on the build that already carries the
 * first redaction: 41 `resource_url` and 11 `http.url` rows.
 *
 * The value stops at the first separator of the OUTER query (`&`, `#`) or of an inner one
 * (`%26`, `%23`). A `Plug.Crypto` token is base64url, so it never contains a percent escape
 * of its own to confuse the boundary.
 *
 * One level of encoding, which is the one that was measured. A URL nested twice would arrive
 * as `%253Ftoken%253D` and still pass: see the note in the pull request on making redaction
 * match the token's SHAPE instead of the parameter's name, which is the only form immune to
 * how deeply the URL is wrapped.
 */
const ENCODED_TOKEN_IN_QUERY = new RegExp(`((?:%3F|%26)${LOGIN_TOKEN_PARAM}%3D)(?:(?!%26|%23)[^&#])*`, 'gi');

/**
 * Where the bootstrap script below leaves the token it took out of the address bar. Exported for
 * the boot watchdog, which has to put it back on the links it offers when the app never starts.
 */
export const EARLY_TOKEN_KEY = '__rwLoginToken';

let consumedToken = '';

type WindowWithEarlyToken = Window & { [EARLY_TOKEN_KEY]?: string };

/** Percent-encoded parameter names are legal, so compare the decoded one. */
function decodeKey(pair: string): string {
  const key = pair.split('=')[0];
  try {
    return decodeURIComponent(key);
  } catch {
    return key;
  }
}

function stripTokenFromSearch(search: string): string {
  return search
    .slice(1)
    .split('&')
    .filter((pair) => decodeKey(pair) !== LOGIN_TOKEN_PARAM)
    .join('&');
}

/**
 * The same cleanup as `consumeLoginTokenFromUrl`, as a string to inline in the document head.
 *
 * It has to be here, and not in the React tree, because of when Next reads the URL. In
 * Next 13.1.1 `initialize()` stores `asPath = getURL()` (client/index.js:130) BEFORE it loads
 * the `_app` chunk (index.js:538), and on a page built with getStaticProps that still has query
 * parameters, `Container.componentDidMount` calls `router.replace(url, asPath, ...)`, which
 * writes `asPath` straight back into the address bar (router.js:1047-1059). Anything running
 * inside the bundle is therefore too late: the token would come back on every real magic link,
 * because all 455 of the distinct login URLs measured on 14/08/2026 carry other parameters
 * beside the token. Inlined in `<Head>` this runs while the HTML is parsed, before the Next
 * runtime reads the URL, so what Next captures is already clean.
 *
 * Kept to ES5 and wrapped in try/catch: it runs before anything else and must never be the
 * reason a page fails to boot.
 */
export const LOGIN_TOKEN_BOOTSTRAP_SCRIPT = `(function(){try{
if(!${LOGIN_PATHNAME.toString()}.test(location.pathname))return;
var v=new URLSearchParams(location.search).get('${LOGIN_TOKEN_PARAM}');
if(!v)return;
window.${EARLY_TOKEN_KEY}=v;
var r=location.search.slice(1).split('&').filter(function(p){var k=p.split('=')[0];try{k=decodeURIComponent(k)}catch(e){}return k!=='${LOGIN_TOKEN_PARAM}'}).join('&');
history.replaceState(history.state,'',location.pathname+(r?'?'+r:'')+location.hash);
}catch(e){}})();`;

/**
 * Take the login token out of the address bar and hand it back, once.
 *
 * Call it as early as the app can run code (module scope of `_app`, before Next builds
 * its router and before any collector mounts), then call it again wherever the token is
 * needed: the value is kept in memory, so the login screen still gets it after the URL
 * has been cleaned. Returns an empty string when there is nothing to consume, which is
 * every page other than the arrival from the e-mail.
 */
export function consumeLoginTokenFromUrl(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  if (consumedToken) {
    return consumedToken;
  }

  // Normal path: the bootstrap script in the document head already did the work.
  const early = (window as WindowWithEarlyToken)[EARLY_TOKEN_KEY];
  if (early) {
    consumedToken = early;
    return consumedToken;
  }

  const { pathname, search, hash } = window.location;
  const token = new URLSearchParams(search).get(LOGIN_TOKEN_PARAM);
  if (!token || !LOGIN_PATHNAME.test(pathname)) {
    return '';
  }

  consumedToken = token;
  // The remaining query is rebuilt by hand rather than through URLSearchParams so the
  // other parameters keep their exact bytes: `redirectParams` carries percent-encoded
  // JSON, and re-encoding it would hand the login screen a different value.
  const remaining = stripTokenFromSearch(search);
  // replaceState and not pushState: the entry the e-mail link created is the one holding
  // the credential, so it has to be overwritten rather than followed by a clean one the
  // Back button can walk out of. The current state object travels along because Next
  // keeps its own routing state in there.
  window.history.replaceState(window.history.state, '', `${pathname}${remaining ? `?${remaining}` : ''}${hash}`);

  return consumedToken;
}

/**
 * Forget the token once the login flow is done with it, success or failure.
 *
 * Without this the value would answer every later caller for the life of the tab, and the
 * guard would keep believing a magic link is being consumed: a worker who has just signed in
 * would never be redirected off `/login`, and a second link opened in the same tab would be
 * ignored in favour of the first.
 */
export function clearConsumedLoginToken(): void {
  consumedToken = '';
  if (typeof window !== 'undefined') {
    delete (window as WindowWithEarlyToken)[EARLY_TOKEN_KEY];
  }
}

/**
 * Replace the value of any `token=` parameter in a URL (or in a message quoting one),
 * whether the parameter arrives raw or percent-encoded inside an enclosing URL.
 */
export function redactUrlToken(url: string): string {
  return url.replace(TOKEN_IN_QUERY, `$1${REDACTED_TOKEN}`).replace(ENCODED_TOKEN_IN_QUERY, `$1${REDACTED_TOKEN}`);
}
